import { useEffect, useId, useRef, useState } from 'react';
import { ORGANIZATION_SEARCH_DEBOUNCE_MS, ORGANIZATION_SEARCH_PAGE_SIZE } from '../constants';
import { useOrganizationById, useOrganizationOptions } from '../hooks/useOrganizations';

type Props = {
  label: string;
  value: number | null | undefined;
  onChange: (organizationId: number | null, name: string) => void;
  testId?: string;
};

export default function OrganizationPicker({ label, value, onChange, testId }: Props) {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const inputId = useId();
  const listboxId = useId();
  const isDirtyRef = useRef(false);
  const selected = useOrganizationById(value);
  const options = useOrganizationOptions({ name: query || null, pageSize: ORGANIZATION_SEARCH_PAGE_SIZE });
  const selectedName = String(selected.data?.id) === String(value) ? selected.data?.name || '' : '';

  useEffect(() => {
    if (
      selected.data?.name &&
      String(selected.data.id) === String(value) &&
      !isDirtyRef.current
    ) {
      setInput(selected.data.name);
    }
  }, [selected.data?.id, selected.data?.name, value]);

  useEffect(() => {
    if (!value && !isDirtyRef.current) setInput('');
  }, [value]);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(input.trim()), ORGANIZATION_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [input]);

  return (
    <div
      className="stack"
      style={{ gap: 6 }}
      data-testid={testId}
      onBlur={(event) => {
        // Input -> option -> outside must also restore the committed value.
        if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
        isDirtyRef.current = false;
        setInput(selectedName);
        setQuery(selectedName);
      }}
    >
      <label className="label" htmlFor={inputId}>{label}</label>
      <input
        id={inputId}
        className={`input ${options.isLoading ? 'loading' : ''}`}
        value={input}
        placeholder="Search organizations"
        onChange={(event) => {
          isDirtyRef.current = true;
          setInput(event.target.value);
        }}
        role="combobox"
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-expanded={options.data.length > 0}
        data-testid={testId ? `${testId}-input` : undefined}
      />
      {value != null && (
        <button
          type="button"
          className="button secondary"
          aria-label={`Clear ${label}`}
          data-testid={testId ? `${testId}-clear` : undefined}
          onClick={() => {
            isDirtyRef.current = false;
            setInput('');
            setQuery('');
            onChange(null, '');
          }}
        >
          Clear
        </button>
      )}
      <div
        id={listboxId}
        role="listbox"
        className="org-results"
        style={{ maxHeight: 160 }}
        data-testid={testId ? `${testId}-options` : undefined}
      >
        {options.data.map((organization) => (
          <button
            type="button"
            role="option"
            aria-selected={String(organization.id) === String(value)}
            className={`org-option ${String(organization.id) === String(value) ? 'active' : ''}`}
            key={organization.id}
            data-testid={testId ? `${testId}-option-${organization.id}` : undefined}
            onClick={() => {
              isDirtyRef.current = false;
              setInput(organization.name);
              setQuery(organization.name);
              onChange(organization.id, organization.name);
            }}
          >
            <span>{organization.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
